import React, { useState } from 'react';
import { Github, Linkedin, Mail, Phone, ExternalLink, ChevronDown, Menu, X, Award, GraduationCap, TrendingUp, Database, BarChart3 } from 'lucide-react';
import { Analytics } from '@vercel/analytics/react';

export default function App () {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const skills = {
    'Data Analysis': ['Pandas', 'Numpy', 'Matplotlib', 'Seaborn'],
    'Programming Languages': ['Python', 'SQL'],
    'Visualization Tools': ['Tableau', 'Power BI', 'Excel'],
    'ML Frameworks': ['Scikit-learn'],
    'Development Tools': ['Git', 'GitHub', 'Jupyter Notebook', 'Flask'],
    'Soft Skills': ['Problem Solving', 'Analytical Thinking', 'Communication', 'Team Collaboration', 'Attention to Detail', 'Critical Thinking']
  };

  const projects = [
    {
      title: 'Netflix Data Analysis (EDA)',
      tech: 'Python, Pandas, SQL, Tableau, Excel, Matplotlib, Seaborn',
      description: 'Analyzed 8,000+ movies and TV shows dataset using Pandas and SQL to uncover content trends and patterns. Identified key insights on genre distribution, release year trends, and country-wise content production. Designed interactive dashboards in Tableau for dynamic visualization and stakeholder presentation. Delivered actionable insights on content strategy through comprehensive statistical analysis.',
      impact: 'Provided comprehensive insights on Netflix content strategy and viewer preferences',
      link: '#'
    },
    {
      title: 'Restaurant Rating Prediction Model',
      tech: 'Python, Pandas, Numpy, Flask, Matplotlib, Scikit-learn',
      description: 'Built an end-to-end machine learning pipeline to predict restaurant aggregate ratings using regression models, achieving accurate rating forecasts to support business decision-making. Performed comprehensive data preprocessing, feature engineering, and exploratory data analysis on restaurant datasets. Deployed the model using Flask framework, creating a user-friendly web interface for real-time predictions.',
      impact: 'Enabled data-driven decisions to improve customer retention strategies and optimize restaurant profitability through predictive insights',
      link: 'https://github.com/sufiyangit46/Restaurant-Rating'
    },
    {
      title: 'Student Score Prediction Model',
      tech: 'Python, Pandas, Scikit-learn, Matplotlib, Seaborn, Numpy',
      description: 'Designed and implemented an end-to-end ML pipeline using Scikit-learn to predict student academic performance with regression algorithms. Conducted data preprocessing, feature engineering, categorical encoding, and model evaluation to optimize prediction accuracy. Automated training and prediction workflow through reusable pipelines and model serialization for scalability.',
      impact: 'Reduced student academic stress by providing personalized score predictions based on study habits and performance factors, enabling proactive academic planning.',
      link: 'https://github.com/sufiyangit46/Score-Prediction-Model'
    },
    {
      title: 'Cuisine Intelligence Engine',
      tech: 'Python, Numpy, Flask, HTML, Scikit-learn',
      description: 'An industry-grade ML system that classifies restaurant cuisines using structured data, probabilistic inference, and a production-ready pipeline with a modern web interface.',
      impact: 'Food discovery platforms and restaurant aggregators do not struggle to accurately tag cuisines, especially when restaurants serve multiple cuisines.',
      link: 'https://github.com/sufiyangit46/Cuisine-Classification'
    },
    {
      title: 'Recommendation System',
      tech: 'Python, Numpy, Scikit-learn, Matplotlib, Pandas',
      description: 'Developed an end-to-end Restaurant Recommendation System that intelligently suggests restaurants to users based on their preferences, historical interactions, and restaurant metadata. The system leverages machine learning techniques to deliver personalized, data-driven recommendations and demonstrates a production-ready ML workflow.',
      impact: 'Enhances user experience through personalization. Increases user engagement and retention. Improves restaurant discovery efficiency Demonstrates real-world application of ML in food-tech platforms',
      link: 'https://github.com/sufiyangit46/Recommendation-System'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-slate-900/95 backdrop-blur-sm shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              MS
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              {['About', 'Skills', 'Experience', 'Projects', 'Education', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="hover:text-blue-400 transition-colors duration-200"
                >
                  {item}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-800 border-t border-slate-700">
            {['About', 'Skills', 'Experience', 'Projects', 'Education', 'Contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="block w-full text-left px-4 py-3 hover:bg-slate-700 transition-colors"
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="about" className="min-h-screen flex items-center justify-center px-4 pt-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <h1 className="text-5xl md:text-7xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
                Mohd Sufiyan
              </span>
            </h1>
            <h2 className="text-2xl md:text-3xl text-blue-300 mb-6">
              Data Science Student & ML Enthusiast
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-8">
              Transforming Data into Actionable Insights
            </p>
          </div>

          {/* About Me Section */}
<section id="about-me" className="py-20 bg-slate-800/50">
  <div className="max-w-3xl mx-auto px-6">

    <h2 className="text-4xl font-bold text-center mb-10">
      <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
        About Me
      </span>
    </h2>

    <div className="space-y-6 text-left">
      <p className="text-gray-300 text-lg leading-relaxed">
        Motivated <span className="text-white font-semibold">Data Science student</span> with hands-on experience in
        <span className="text-white font-semibold">
          {" "}Python, Machine Learning, and end-to-end ML pipeline development
        </span>. Passionate about leveraging analytical skills and programming expertise to solve real-world business
        problems through data-driven solutions.
      </p>

      <p className="text-gray-300 text-lg leading-relaxed">
        During my internship at <span className="text-white font-semibold">Cognifyz Technologies</span>, I worked on
        real-world problems including
        <span className="text-white font-semibold">
          {" "}recommendation systems, cuisine classification, and rating prediction models
        </span>.
      </p>

      <p className="text-gray-300 text-lg leading-relaxed">
        I enjoy transforming raw data into meaningful insights and
        <span className="text-white font-semibold"> building solutions</span> that create measurable
        <span className="text-white font-semibold"> business impact</span>.
      </p>
    </div>

  </div>
</section>



          {/* Social Links */}
          <div className="flex justify-center space-x-6 mb-12">
            <a href="https://linkedin.com/in/sufiyan-shaikh-51a889321" target="_blank" rel="noopener noreferrer"
               className="bg-slate-800 p-4 rounded-full hover:bg-blue-600 transition-all duration-300 hover:scale-110">
              <Linkedin size={24} />
            </a>
            <a href="https://github.com/sufiyangit46" target="_blank" rel="noopener noreferrer"
               className="bg-slate-800 p-4 rounded-full hover:bg-blue-600 transition-all duration-300 hover:scale-110">
              <Github size={24} />
            </a>
            <a href="mailto:shaikhsufiyan1246@gmail.com"
               className="bg-slate-800 p-4 rounded-full hover:bg-blue-600 transition-all duration-300 hover:scale-110">
              <Mail size={24} />
            </a>
            <a href="tel:+918767883949"
               className="bg-slate-800 p-4 rounded-full hover:bg-blue-600 transition-all duration-300 hover:scale-110">
              <Phone size={24} />
            </a>
          </div>

          <button
            onClick={() => scrollToSection('skills')}
            className="animate-bounce bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 inline-flex items-center space-x-2"
          >
            <span>Explore My Work</span>
            <ChevronDown size={20} />
          </button>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center">
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Technical Skills
            </span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(skills).map(([category, items]) => (
              <div key={category} className="bg-slate-900/50 backdrop-blur-sm p-6 rounded-xl border border-blue-500/20 hover:border-blue-500/50 transition-all duration-300">
                <h3 className="text-xl font-semibold mb-4 text-blue-400">{category}</h3>
                <ul className="space-y-2">
                  {items.map((skill) => (
                    <li key={skill} className="text-gray-300 flex items-center">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3"></span>
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 p-6 rounded-xl border border-blue-400/30">
              <Database className="w-12 h-12 text-blue-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Data Analysis</h3>
              <p className="text-gray-300">Pandas, Numpy, Matplotlib for data manipulation and insights</p>
            </div>
            <div className="bg-gradient-to-br from-cyan-600/20 to-teal-600/20 p-6 rounded-xl border border-cyan-400/30">
              <BarChart3 className="w-12 h-12 text-cyan-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Visualization Tools</h3>
              <p className="text-gray-300">Tableau, Power BI dashboards for business intelligence</p>
            </div>
            <div className="bg-gradient-to-br from-teal-600/20 to-green-600/20 p-6 rounded-xl border border-teal-400/30">
              <TrendingUp className="w-12 h-12 text-teal-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Business Analytics</h3>
              <p className="text-gray-300">Competitive analysis, trend research, KPI tracking</p>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center">
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Professional Experience
            </span>
          </h2>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-blue-500/20">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
              <div>
                <h3 className="text-2xl font-bold text-blue-400 mb-2">Machine Learning Intern</h3>
                <p className="text-xl text-gray-300">Cognifyz Technologies</p>
                <p className="text-gray-400">Nagpur, Maharashtra, India</p>
              </div>
              <span className="bg-blue-600/20 text-blue-300 px-4 py-2 rounded-full text-sm font-semibold">
                Dec 2025 - Jan 2026
              </span>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                <span className="text-gray-300">Designed and implemented a Recommendation System using collaborative and content-based filtering techniques to deliver personalized suggestions based on user behavior and item similarity.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                <span className="text-gray-300">Built a Cuisine Classification Model to categorize restaurants based on menu and metadata features, applying data preprocessing, feature engineering, and supervised learning techniques.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                <span className="text-gray-300">Debugged pipeline failures, handled data inconsistencies, and resolved environment and dependency issues during development.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                <span className="text-gray-300">Deployed models locally using Flask APIs to simulate real production environments.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                <span className="text-gray-300">Developed a Restaurant Rating Prediction Model to estimate customer ratings using regression algorithms, focusing on performance optimization and interpretability.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                <span className="text-gray-300">Performed data cleaning, EDA, feature selection, and model tuning to improve accuracy and generalization.</span>
              </li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-2">
              {['Python', 'Pandas', 'Data Preprocessing', 'Feature Engineering', 'Scikit-learn', 'Data Pipelines', 'Flask', 'Recommender System', 'Numpy', 'ML Algorithms'].map((tech) => (
                <span key={tech} className="bg-slate-700 text-blue-300 px-3 py-1 rounded-full text-sm">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center">
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Featured Projects
            </span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-slate-900/50 backdrop-blur-sm rounded-xl overflow-hidden border border-blue-500/20 hover:border-blue-500/50 transition-all duration-300 hover:scale-105">
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3 text-blue-400">{project.title}</h3>
                  <p className="text-sm text-gray-400 mb-4">{project.tech}</p>
                  <p className="text-gray-300 mb-4">{project.description}</p>

                  <div className="bg-green-600/20 border border-green-500/30 rounded-lg p-3 mb-4">
                    <p className="text-sm text-green-300"><strong>Impact:</strong> {project.impact}</p>
                  </div>
                  {project.link !== '#' && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <span>View on GitHub</span>
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education & Achievements */}
      <section id="education" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center">
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Education & Achievements
            </span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-blue-500/20">
              <GraduationCap className="w-12 h-12 text-blue-400 mb-4" />
              <h3 className="text-2xl font-bold mb-4 text-blue-400">Education</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-xl font-semibold">B.Sc in Data Science</h4>
                  <p className="text-gray-300">Mumbai University</p>
                  <p className="text-gray-400">2024 - 2027 (Currently Pursuing)</p>
                  <p className="text-cyan-400 font-semibold mt-2">CGPA: 8.68/10</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold">Class XII</h4>
                  <p className="text-gray-300">Maharashtra Board</p>
                  <p className="text-cyan-400 font-semibold mt-2"> 60% </p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold">Class X </h4>
                  <p className="text-gray-300">Maharashtra Board</p>
                  <p className="text-cyan-400 font-semibold mt-2"> 60% </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-blue-500/20">
              <Award className="w-12 h-12 text-cyan-400 mb-4" />
              <h3 className="text-2xl font-bold mb-4 text-cyan-400">Achievements</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                  <span className="text-gray-300"><strong className="text-white">Ranked Top 3</strong> in class during Data Science program</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                  <span className="text-gray-300"><strong className="text-white">Brandquetz Competition</strong> Participant - Business Analytics</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                  <span className="text-gray-300"><strong className="text-white">GitHub Portfolio</strong> - 3+ documented projects</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full mr-3 mt-2"></span>
                  <span className="text-gray-300"><strong className="text-white">Tata Campus Quiz 2025</strong> Participant</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8">
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Let's Connect
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            I enjoy discussing data-driven solutions, machine learning projects, AI and real-world problem solving.
            Open to internship opportunities in Machine Learning, Artificial Intelligence and Data Engineering.
            I’d be happy to connect.
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <a href="mailto:shaikhsufiyan1246@gmail.com" className="bg-slate-900/50 backdrop-blur-sm p-6 rounded-xl border border-blue-500/20 hover:border-blue-500/50 transition-all duration-300 hover:scale-105">
              <Mail className="w-8 h-8 text-blue-400 mb-3 mx-auto" />
              <h3 className="text-lg font-semibold mb-2">Email</h3>
              <p className="text-gray-400">shaikhsufiyan1246@gmail.com</p>
            </a>
            <a href="tel:+918767883949" className="bg-slate-900/50 backdrop-blur-sm p-6 rounded-xl border border-blue-500/20 hover:border-blue-500/50 transition-all duration-300 hover:scale-105">
              <Phone className="w-8 h-8 text-cyan-400 mb-3 mx-auto" />
              <h3 className="text-lg font-semibold mb-2">Phone</h3>
              <p className="text-gray-400">+91-8767883949</p>
            </a>
          </div>
          <div className="flex justify-center space-x-6">
            <a href="https://linkedin.com/in/sufiyan-shaikh-51a889321" target="_blank" rel="noopener noreferrer"
               className="bg-blue-600 hover:bg-blue-700 p-4 rounded-full transition-all duration-300 hover:scale-110">
              <Linkedin size={28} />
            </a>
            <a href="https://github.com/sufiyangit46" target="_blank" rel="noopener noreferrer"
               className="bg-slate-700 hover:bg-slate-600 p-4 rounded-full transition-all duration-300 hover:scale-110">
              <Github size={28} />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-700 text-center">
        <p className="text-gray-400">
          © 2026 Mohd Sufiyan. Built with Love and Code
        </p>
      </footer>

      <Analytics />
    </div>
  );
}

